import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ProjectContext } from '@models/projects';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { ProjectAccessService } from './project-access.service';

export const hasProjectAdminAccessGuard: CanActivateFn = (route) => {
  const dataContext = inject(DataContext);
  const projectAccess = inject(ProjectAccessService);
  const router = inject(Router);
  const message = inject(MessageService);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') ?? '';
  const projectContext = dataContext.matchProjectContext(projectId);

  if (projectContext) {
    if (projectContext.hasAdminAccess()) {
      void resolveAccessAsync();
      return true;
    }

    message.showErrorMessage(translate.instant('general.adminAccessRequired'));
    return false;
  }

  return resolveAccessAsync();

  async function loadProjectAsync(): Promise<void> {
    // Project details must be loaded first so the project context exists before client access is applied to it.
    await projectAccess.loadProjectDetailsAsync(projectId);
    await projectAccess.loadCompanyAccessAsync(projectId);
  }

  async function resolveAccessAsync(): Promise<boolean> {
    await loadProjectAsync();

    if (!dataContext.isRequestedProject(projectId)) {
      return false;
    }

    const context = dataContext.projectContext();
    if (!context || !context.project()) {
      message.showErrorMessage(translate.instant('general.projectLoadFailed'));
      void router.navigate(['/projects']);
      return false;
    }

    if (context.hasAdminAccess()) {
      return true;
    }

    redirectWithoutAdminAccess(context);
    return false;
  }

  function redirectWithoutAdminAccess(context: ProjectContext): void {
    message.showErrorMessage(translate.instant('general.adminAccessRequired'));

    if (context.hasClientAccess()) {
      void router.navigate(['/projects', projectId, 'doors']);
    } else {
      void router.navigate(['/projects']);
    }
  }
};
